import React, { useEffect, useMemo, useRef, useState } from 'react'

import { View } from '@amzn/stencil-react-components/layout'
import {
    Controls,
    deserializeFromMarkdown,
    RichTextEditor,
    RichTextEditorProps,
    RichTextEditorRef,
} from '@amzn/stencil-react-components/rte'
import { TextArea } from '@amzn/stencil-react-components/text-area'

import { Locale } from 'src/models/dto/Locale'

export type ToolbarControls = RichTextEditorProps['controls']

export const SimpleToolbar: ToolbarControls = {
    blockquote: false,
    bulletList: false,
    underline: false,
    link: false,
    heading: false,
    horizontalRule: false,
    keyboardShortcuts: false,
    fullScreen: false,
    clearFormat: false,
    orderedList: false,
    strike: false,
    history: false,
    textAlign: false,
    indent: false,
}

export interface MarkdownEditorProps {
    disabled?: boolean
    title?: string
    value: string
    onChange: React.Dispatch<string>
    id?: string
    dataTestId?: string
    fallbackToTextArea?: boolean
    height?: string | number
    locale?: Locale
    placeholder?: string
    toolbarControls?: Partial<Controls>
}

const defaultHeight = '320px'

export function StencilMarkdownEditor({
    value: mdValue,
    onChange: onMdValueChange,
    id,
    dataTestId,
    height = defaultHeight,
    toolbarControls,
    disabled,
}: MarkdownEditorProps) {
    const [defaultHtmlValue, _] = useState(() => deserializeFromMarkdown(mdValue) ?? '')
    const editorRef = useRef<RichTextEditorRef>(null)
    const controls = useMemo(
        (): Partial<Controls> => ({
            ...toolbarControls,
            fullScreen: false,
        }),
        [toolbarControls]
    )

    const lastMdValue = useRef(mdValue)
    const isSettingContent = useRef(false)
    useEffect(() => {
        if (mdValue === lastMdValue.current) {
            return
        }
        const newHtmlValue = deserializeFromMarkdown(mdValue) ?? ''
        lastMdValue.current = mdValue
        /* istanbul ignore if */
        if (!editorRef.current) {
            return
        }

        isSettingContent.current = true
        try {
            editorRef.current.editorInstance.commands.content.set(newHtmlValue)
        } finally {
            isSettingContent.current = false
        }
    }, [mdValue])
    return (
        <View id={id} dataTestId={dataTestId} style={{ minHeight: height }}>
            <RichTextEditor
                controls={controls}
                defaultContent={defaultHtmlValue}
                onChange={(editor1) => {
                    /* istanbul ignore if */
                    if (isSettingContent.current) {
                        return
                    }
                    // Persist as HTML (e.g. <p><strong><em>...). deserializeFromMarkdown
                    // on load handles HTML transparently since markdown passes raw HTML
                    // through, keeping us backwards-compatible with markdown content too.
                    const newHtml = editor1.getHTML()
                    // If the RTE is left empty, there can be an empty <p>, falsely passing "required" validations
                    const trimmed = newHtml === '<p></p>' ? '' : newHtml
                    lastMdValue.current = trimmed
                    onMdValueChange(trimmed)
                }}
                ref={editorRef}
                disabled={disabled}
            />
        </View>
    )
}

function TextAreaMarkdownEditor({
    value: markdownControlled,
    onChange: onMarkdownChange,
    id,
    dataTestId,
    height = defaultHeight,
    placeholder,
    disabled,
}: MarkdownEditorProps) {
    return (
        <TextArea
            placeholder={placeholder ?? 'Markdown Content'}
            value={markdownControlled}
            onChange={(e) => onMarkdownChange(e.target.value)}
            dataTestId={dataTestId}
            id={id}
            height={height}
            disabled={disabled}
        />
    )
}

export function MarkdownEditor(props: MarkdownEditorProps) {
    const { fallbackToTextArea } = props
    const canRenderMarkdownEditor =
        (window as never as Record<string, unknown>).ResizeObserver &&
        (document as never as Record<string, unknown>).elementFromPoint
    if (!canRenderMarkdownEditor || fallbackToTextArea) {
        return <TextAreaMarkdownEditor {...props} />
    }
    /* istanbul ignore next */
    return <StencilMarkdownEditor {...props} />
}
